import { FC, ReactNode } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TablePagination from 'components/TablePagination';
import TableToolbar from 'components/TableToolbar';
import TableSearchField from 'components/TableSearchField';
import TableEmpty from 'components/TableEmpty';

import { Table_pageInfo$key } from '__generated__/Table_pageInfo.graphql';

type Props = {
  pageInfo: Table_pageInfo$key | null;
  search?: boolean;
  pagination?: boolean;
  loading?: boolean;
  hideToolbar?: boolean;
  head: ReactNode;
  icon?: ReactNode;
  toolbar?: ReactNode;
  sx?: SxProps<Theme>;
  noRecordsText?: ReactNode;
  noRecordsIcon?: ReactNode;
};

export type TableProps = Props;

const Table: FC<Props> = props => {
  const {
    children,
    head,
    toolbar,
    hideToolbar,
    search,
    pagination,
    icon,
    loading,
    sx,
    noRecordsText,
    noRecordsIcon,
  } = props;

  const pageInfo = useFragment(
    graphql`
      fragment Table_pageInfo on PageInfo {
        total

        ...TablePagination_pageInfo
      }
    `,
    props.pageInfo
  );

  const hasRows = Array.isArray(children) && children.length > 0;

  return (
    <Box sx={sx}>
      {!hideToolbar && (
        <TableToolbar icon={icon} loading={loading}>
          {toolbar}
          {search && <TableSearchField />}
          {pagination && <TablePagination pageInfo={pageInfo ?? null} />}
        </TableToolbar>
      )}

      <TableContainer sx={{ overflowX: 'visible' }}>
        <MuiTable>
          <TableHead>{head}</TableHead>
          <TableBody>
            {hasRows ? (
              children
            ) : (
              <TableEmpty
                headSize={(head as any)?.props?.children?.length}
                noRecordsText={noRecordsText}
                noRecordsIcon={noRecordsIcon}
              />
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {pagination && <TablePagination pageInfo={pageInfo ?? null} />}
    </Box>
  );
};

export default Table;
